import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { Button, useToast, Dialog, DialogContent, DialogHeader, DialogTitle } from '@org/ui';
import { Ban, AlertTriangle } from 'lucide-react';

interface BlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onSuccess?: () => void;
}

export function BlockModal({ isOpen, onClose, userId, userName, onSuccess }: BlockModalProps) {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);

  const blockUser = communityTrpc.moderation.blockUser.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      onSuccess?.();
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const handleBlock = () => {
    blockUser.mutate({ blockedId: userId });
  };

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-destructive" />
            {t('blockModal.title')}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Ban className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium">{t('blockModal.userBlocked')}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('blockModal.blockedMessage', { name: userName })}
            </p>
            <Button onClick={handleClose} className="mt-6">
              {t('blockModal.close')}
            </Button>
          </div>
        ) : (
          <div>
            <div className="mb-4 flex items-start gap-3 rounded-lg bg-destructive/10 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
              <div>
                <p className="font-medium text-destructive">{t('blockModal.confirmMessage', { name: userName })}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('blockModal.consequencesTitle')}
                </p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>- {t('blockModal.consequence1')}</li>
                  <li>- {t('blockModal.consequence2')}</li>
                  <li>- {t('blockModal.consequence3')}</li>
                  <li>- {t('blockModal.consequence4')}</li>
                </ul>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              {t('blockModal.canUnblock')}
            </p>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleBlock}
                disabled={blockUser.isPending}
                className="gap-2"
              >
                <Ban className="h-4 w-4" />
                {blockUser.isPending ? t('blockModal.blocking') : t('blockModal.block')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function useBlockModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null);

  const openBlock = (id: string, name: string) => {
    setTarget({ id, name });
    setIsOpen(true);
  };

  const closeBlock = () => {
    setIsOpen(false);
    setTarget(null);
  };

  return {
    isOpen,
    target,
    openBlock,
    closeBlock,
  };
}
